/////////////////////////////////////////
import { Button, Stack, Typography } from '@mui/material';
import React, { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import PersistLoading from 'components/PersistLoading';
import { redeemOpaqueRefreshToken } from 'app-components/flux/auth/opaqueRefreshToken';
import { getClientConfig, localPreferences } from 'app-components/utils/core';
import type { RootStore } from 'store';
import { clearEntitlements } from 'data/entitlements/entitlementsUtils';
import * as sessionStorageEx from 'utils/sessionStorageEx';
import { getDevSsoState, type DevSsoStatus } from './capture';
import { getPreviewSignInUrl } from './previewTarget';

export function subscribeDevSso(listener: () => void): () => void {
  const state = getDevSsoState();
  if (!state) return () => {};
  const listeners = (state.listeners ??= new Set<() => void>());
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function getDevSsoStatus(): DevSsoStatus {
  return getDevSsoState()?.status ?? 'failed';
}
function notify(): void {
  getDevSsoState()?.listeners?.forEach((listener) => listener());
}

export function startDevSso(store: Pick<RootStore, 'dispatch'>): Promise<void> {
  const state = getDevSsoState();
  if (!state) return Promise.resolve();
  if (state.promise) return state.promise;
  if (state.status !== 'captured') return Promise.resolve();
  const refreshToken = state.takeToken?.();
  delete state.takeToken;
  const serviceEnvironment = process.env.SERVICE_ENV || 'stg';
  if (
    !refreshToken ||
    getClientConfig()?.environment === 'prod' ||
    getClientConfig()?.environment !== serviceEnvironment
  ) {
    state.status = 'failed';
    notify();
    return Promise.resolve();
  }
  state.status = 'pending';
  // Assign before notifying React; even a reentrant subscriber shares this attempt.
  state.promise = Promise.resolve()
    .then(() => store.dispatch(redeemOpaqueRefreshToken(refreshToken)).unwrap())
    .then(() => {
      // Refresh account-specific entitlements before releasing the authenticated app.
      clearEntitlements();
      state.status = 'succeeded';
    })
    .catch(() => {
      state.status = 'failed';
    })
    .finally(notify);
  notify();
  return state.promise;
}

export default function DevSsoGate({
  store,
  children,
}: {
  store: Pick<RootStore, 'dispatch'>;
  children: ReactNode;
}): ReactNode {
  const status = useSyncExternalStore(subscribeDevSso, getDevSsoStatus);
  useEffect(() => {
    void startDevSso(store);
  }, [store]);
  if (status === 'succeeded') return children;
  if (status === 'failed') {
    // Previews can mint a fresh link from staging.
    const signInUrl = getPreviewSignInUrl();
    // A failed attempt keeps any saved session, so leaving either restores it or shows sign-in.
    const hasSavedSession = Boolean(localPreferences.getAuthSession() || sessionStorageEx.getAuthSession());
    return (
      <Stack alignItems='center' justifyContent='center' minHeight='100vh' spacing={2} p={3} role='alert'>
        <Typography variant='titleBold'>Preview sign-in failed</Typography>
        <Typography>This link may have expired or already been used.</Typography>
        <Stack direction='row' spacing={2}>
          {signInUrl && (
            <Button component='a' href={signInUrl} variant='contained'>
              Try again
            </Button>
          )}
          <Button component='a' href={hasSavedSession ? '/' : location.href} variant={signInUrl ? 'text' : 'contained'}>
            {hasSavedSession ? 'Go to dashboard' : 'Sign in'}
          </Button>
        </Stack>
      </Stack>
    );
  }
  return <PersistLoading />;
}
/////////
